import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Divider,
  Paper,
  Card,
  CardContent,
  Grid,
  Alert,
  useTheme,
  InputAdornment,
} from "@mui/material";
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  Link as LinkIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { GET_ALL_AVAILABLE_INTEGRATIONS } from "../../../../../apis/settingsApis/integration-apis/integrationApis";
import { GET_STAFF_DETAILS } from "../../../../../apis/organization-apis/people-apis/peopleApis";
import Role from "../../../../../utils/roles";
import BulkUserAccessDialog from "../../shared/components/BulkUserAccessDialog";

export default function AddCriticalSystemDialog({ open, onClose, onSubmit, loading, onConnectIntegration }) {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    name: '',
    system_admin: '',
    is_critical: true,
    type: '',
    review_cycle: "365",
    category: '',
    account_label: '', // Account label for systems with multiple accounts
    users: [] // Array to store added users
  });

  // Fetch employees to get admins
  const { data: staffData } = useQuery(GET_STAFF_DETAILS);
  const employees = staffData?.getEmployees?.data || [];

  // Filter to get only admins - check if employee has admin role
  const admins = useMemo(() => {
    return employees
      .filter(emp => {
        // Check if employee has a role with role_description === "admin"
        return emp.roles?.some(role => role?.role_description?.toLowerCase() === Role.ADMIN.toLowerCase());
      })
      .map(emp => {
        const primaryEmail = emp.emails?.find((em) => em.type === "work")?.email || emp.emails?.[0]?.email || "";
        return {
          email: primaryEmail,
          name: emp.name || `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        };
      })
      .filter(admin => admin.email); // Only include admins with emails
  }, [employees]);
  // 0 = choose, 1 = details, 2 = confirm, 3 = add users, 4 = final review
  const [step, setStep] = useState(0);

  // Fetch all available integrations once (client-side partial search)
  const { data: availableData } = useQuery(GET_ALL_AVAILABLE_INTEGRATIONS);
  const availableList = availableData?.getAllAvailableIntegrations?.list || [];
  const suggestions = useMemo(() => {
    const q = (formData.name || '').toLowerCase().trim();
    if (!q) return [];
    return availableList
      .filter(i => i.label && i.label.toLowerCase().includes(q))
      .slice(0, 6);
  }, [availableList, formData.name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step === 0) { setStep(1); return; }
    if (step === 1) { setStep(2); return; }
    if (step === 2) { setStep(3); return; }
    if (step === 3) { setStep(4); return; }
    // Final submission
    onSubmit({
      label: formData.name,
      system_admin: formData.system_admin,
      is_critical: formData.is_critical,
      category: formData.category || formData.type,
      review_cycle: parseInt(formData.review_cycle, 10) || null,
      account_label: formData.account_label || null,
      is_review_active: true,
      users: formData.users
    });
    setFormData({
      name: '',
      system_admin: '',
      is_critical: true,
      type: '',
      review_cycle: "365",
      category: '',
      account_label: '',
      users: []
    });
    setStep(0);
  };

  const handleClose = () => {
    setFormData({
      name: '',
      system_admin: '',
      is_critical: true,
      type: '',
      review_cycle: "365",
      category: '',
      account_label: '',
      users: []
    });
    setStep(0);
    onClose();
  };

  const removeUser = (index) => {
    setFormData({
      ...formData,
      users: formData.users.filter((_, i) => i !== index)
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "16px",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        Add new account
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <ClearIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent
          dividers
          sx={{ background: theme.customColors.background.main }}
        >
          {step === 0 && (
            <>
              <Alert
                severity="info"
                sx={{
                  mb: 3,
                  backgroundColor: theme.customColors.background.info,
                  border: `1px solid ${theme.customColors.border.accent}`,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ fontSize: "14px", color: theme.customColors.text.strong }}
                >
                  Choose how you would like to add a new system.
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: `1px solid ${theme.customColors.border.main}`,
                      borderRadius: "12px",
                      background: theme.customColors.background.main,
                      boxShadow: theme.customColors.shadow.main,
                      transition: "all 0.2s ease",
                      height: "100%",
                      "&:hover": {
                        borderColor: theme.palette._blue.main,
                        backgroundColor: theme.customColors.background.hover,
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => { handleClose(); if (onConnectIntegration) onConnectIntegration(); }}
                  >
                    <CardContent
                      sx={{
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        pb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            backgroundColor: theme.palette._blue.light,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <LinkIcon
                            sx={{ fontSize: 20, color: theme.palette._blue.main }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                              fontSize: "15px",
                              color: theme.customColors.text.strong,
                              mb: 0.5,
                            }}
                          >
                            Connect via Integration
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "12px", lineHeight: 1.3 }}
                          >
                            Connect supported systems via integrations. Users will be auto-synced.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      border: `1px solid ${theme.customColors.border.main}`,
                      borderRadius: "12px",
                      background: theme.customColors.background.main,
                      boxShadow: theme.customColors.shadow.main,
                      transition: "all 0.2s ease",
                      height: "100%",
                      "&:hover": {
                        borderColor: theme.palette.warning.main,
                        backgroundColor: theme.customColors.background.hover,
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                    }}
                    onClick={() => setStep(1)}
                  >
                    <CardContent
                      sx={{
                        p: 2,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        pb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "10px",
                            backgroundColor: theme.customColors.status.warning.bg,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <AddIcon
                            sx={{
                              fontSize: 20,
                              color: theme.customColors.status.warning.main,
                            }}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            sx={{
                              fontSize: "15px",
                              color: theme.customColors.text.strong,
                              mb: 0.5,
                            }}
                          >
                            Create Manually
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "12px", lineHeight: 1.3 }}
                          >
                            Create a custom system with owner and metadata.
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
          {step === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              <TextField
                label="System name"
                value={formData.name}
                onChange={(e) => {
                  const newName = e.target.value;
                  // If user changes name after selecting a suggestion, clear auto-filled data
                  const isTypingNewName = newName !== formData.name && !!formData.category;
                  setFormData({
                    ...formData,
                    name: newName,
                    ...(isTypingNewName && { category: '', type: '' })
                  });
                }}
                required
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#000000',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette._blue.main,
                      borderWidth: 2,
                    }
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 500
                  }
                }}
              />
              {formData.name && suggestions.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 1, borderRadius: 2 }}>
                  <Box sx={{ p: 1.5 }}>
                    <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>Matches</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      {suggestions.map((s, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            py: 1, px: 1,
                            borderTop: idx === 0 ? 'none' : `1px dashed ${theme.customColors.border.dashed}`,
                            cursor: 'pointer',
                            '&:hover': { background: theme.customColors.background.hover }
                          }}
                          onClick={() => setFormData({ ...formData, name: s.label, category: s.category, type: s.category })}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.label}</Typography>
                          <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>{s.category}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Paper>
              )}

              <TextField
                label="Account Label"
                value={formData.account_label}
                onChange={(e) => setFormData({ ...formData, account_label: e.target.value })}
                placeholder="e.g., Production, Staging, Admin Account"
                helperText="Optional: Add a label to identify this account if the system has multiple accounts"
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#000000',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette._blue.main,
                      borderWidth: 2,
                    }
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 500
                  }
                }}
              />

              <FormControl fullWidth>
                <InputLabel>Account Owner *</InputLabel>
                <Select
                  value={formData.system_admin}
                  onChange={(e) => setFormData({ ...formData, system_admin: e.target.value })}
                  label="Account Owner"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.customColors.border.main },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000000' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette._blue.main, borderWidth: 1 },
                    '& .MuiSelect-select': { backgroundColor: '#FFFFFF' },
                  }}
                  required
                >
                  {admins.map((admin, index) => (
                    <MenuItem key={index} value={admin.email}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {admin.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {admin.email}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Is this a Critical Account? *</InputLabel>
                    <Select
                      value={formData.is_critical ? 'critical' : 'not_critical'}
                      onChange={(e) => setFormData({ ...formData, is_critical: e.target.value === 'critical' })}
                      label="Critical Account?"
                      sx={{
                        borderRadius: 2,
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.customColors.border.main },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#000000' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette._blue.main, borderWidth: 2 },
                        '& .MuiSelect-select': { backgroundColor: '#FFFFFF' },
                      }}
                      required
                    >
                      <MenuItem value="not_critical">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10B981' }} />
                          <Typography>No</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="critical">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#DC2626' }} />
                          <Typography>Yes</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    label="How often should this system be reviewed?"
                    type="number"
                    value={formData.review_cycle}
                    onChange={(e) => setFormData({ ...formData, review_cycle: e.target.value })}
                    placeholder="Enter days"
                    inputProps={{ min: 1, max: 365 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="body2" sx={{ color: theme.customColors?.text?.secondary }}>
                            days
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        backgroundColor: '#FFFFFF',
                        '& fieldset': {
                          borderColor: theme.customColors.border.main,
                        },
                        '&:hover fieldset': {
                          borderColor: '#000000',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette._blue.main,
                          borderWidth: 2,
                        },
                      },
                      '& .MuiInputLabel-root': {
                        backgroundColor: '#FFFFFF',
                        px: 1,
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
          {step === 2 && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              p: 3,
              border: `1px solid ${theme.customColors.border.main}`,
              borderRadius: 3,
              bgcolor: theme.customColors.background.main,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
              <Typography variant="subtitle1" fontWeight={700}>Confirm & Add</Typography>
              <Divider />
              <Box>
                <Typography variant="caption" color="text.secondary">Name</Typography>
                <Typography variant="body2" fontWeight={500}>{formData.name}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Account Owner</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {admins.find(a => a.email === formData.system_admin)?.name || formData.system_admin}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Critical Account</Typography>
                <Typography variant="body2" fontWeight={500}>{formData.is_critical ? 'Critical' : 'Not Critical'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography variant="body2" fontWeight={500}>{formData.type || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Review Cycle</Typography>
                <Typography variant="body2" fontWeight={500}>{formData.review_cycle} Days</Typography>
              </Box>
              {formData.account_label && (
                <Box>
                  <Typography variant="caption" color="text.secondary">Account Label</Typography>
                  <Typography variant="body2" fontWeight={500}>{formData.account_label}</Typography>
                </Box>
              )}

              {/* Additional Information */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>Additional Information</Typography>

              <TextField
                label="System Type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                fullWidth
                disabled={Boolean(formData.category)}
                placeholder="e.g., infra, hris, ticketing, file-storage, ci/cd, cloud storage, etc."
                helperText={
                  formData.category
                    ? "Auto-filled from existing integration"
                    : "Enter the system type/category"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    backgroundColor: '#FFFFFF',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette._blue.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette._blue.main,
                      borderWidth: 2,
                    }
                  },
                  "& .MuiInputLabel-root": {
                    fontWeight: 500
                  }
                }}
              />
            </Box>
          )}
          {step === 3 && (
            <BulkUserAccessDialog
              formData={formData}
              setFormData={setFormData}
              theme={theme}
              inScopeOnly={true}
            />
          )}
          {step === 4 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="h5" fontWeight={700}>Final Review</Typography>
              <Divider />

              {/* System Details */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>System Information</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Name</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Account Owner</Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {admins.find(a => a.email === formData.system_admin)?.name || formData.system_admin}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Critical Account</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.is_critical ? 'Critical' : 'Not Critical'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Type</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.type || '-'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Review Cycle</Typography>
                    <Typography variant="body2" fontWeight={500}>{formData.review_cycle} Days</Typography>
                  </Box>
                  {formData.account_label && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Account Label</Typography>
                      <Typography variant="body2" fontWeight={500}>{formData.account_label}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Users */}
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Users ({formData.users.length})</Typography>
                {formData.users.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {formData.users.map((user, index) => (
                      <Box key={index} sx={{ p: 1.5, border: `1px solid ${theme.customColors.border.main}`, borderRadius: 1 }}>
                        <Typography variant="body2" fontWeight={500}>{user.name}</Typography>
                        <Typography variant="caption" sx={{ color: theme.customColors.text.secondary }}>
                          {user.email} • {user.role}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.customColors.text.secondary }}>
                    No users added
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            background: theme.customColors.background.cardHeader,
            borderTop: `1px solid ${theme.customColors.border.main}`,
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              fontSize: "14px",
              color: theme.customColors.text.secondary,
              borderColor: theme.customColors.border.main,
              "&:hover": {
                backgroundColor: theme.customColors.background.hover,
                color: theme.customColors.text.strong,
              },
            }}
          >
            Cancel
          </Button>
          {(step === 1 || step === 2 || step === 3 || step === 4) && (
            <Button
              onClick={() => setStep(step - 1)}
              variant="outlined"
              sx={{
                fontSize: "14px",
                color: theme.customColors.text.secondary,
                borderColor: theme.customColors.border.main,
                "&:hover": {
                  backgroundColor: theme.customColors.background.hover,
                  color: theme.customColors.text.strong,
                },
              }}
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            sx={{
              backgroundColor: "#031B89",
              "&:hover": { backgroundColor: "#0056b3" },
              fontSize: "14px",
            }}
            disabled={loading || (step === 3 && formData.users.length === 0)}
          >
            {step === 0 ? 'Continue' : step === 1 ? 'Next' : step === 2 ? 'Add Users' : step === 3 ? 'Final Review' : 'Create System'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};